import * as React from 'react';
import * as c from 'models/constants';
import { translate } from 'react-i18next';
import { IStore } from 'redux/IStore';
import { connect, Dispatch } from 'react-redux';
import { IShopCard } from 'models/shop/shopcard';
import { shopBookingTemplatesGet } from 'redux/modules/shop/bookingtemplates';
import { IServicesList } from 'models/shop/shopBookingTemplates';
import { IServiceCategoryDomainValue, ITranslateProps } from 'models/common';
import { Button, LoadingOverlayWrapper } from 'components';
import { createDomainValueOptions, formatCurrency } from 'helpers/common';
import { browserHistory, Link } from 'react-router';
import { SelectableServicesList } from 'containers/Shop/Common/SelectableServicesList';
import { getOfficeServiceGroups } from 'redux/modules/shop/serviceGroups';
import { IServiceGroup } from 'models/shop/serviceGroups';
import { groupServices } from 'redux/modules/shop/serviceGroups/helpers';
import { domainValuesMap } from 'redux/modules/domainvalues';
import { generateId, isBookable } from 'helpers/bookingTemplates';
import { Expandable } from 'components/Common/Expandable';
import { PortalSelectMenu } from 'components/FormControls';

interface IOwnProps {
	officeId: string;
	shopId: IShopCard['id'];
	activeMembers?: any[];
	restrictedMode?: boolean;
}

interface IState {
	chosenVariantParents: string[];
	selectedServiceCategory?: IServiceCategoryDomainValue['key'];
	activeServiceGroup?: IServiceGroup['id'];
	isScrolling: boolean;
}

interface IProps
	extends IOwnProps,
		Partial<ITranslateProps>,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {}

const filterName = 'genderFilter';
let genderFilterValue: typeof c.SERVICES_ALL | typeof c.SERVICES_WOMAN | typeof c.SERVICES_MAN = 'all';

const servicesIdent = (ss: IServicesList[]): string => (ss || []).map((s) => s.id).join(',');

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ShopCardServicesForm extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.state = {
			chosenVariantParents: [],
			isScrolling: false,
		};

		this.handleGenderFilter = this.handleGenderFilter.bind(this);
		this.toggleService = this.toggleService.bind(this);
		this.setActiveServiceGroup = this.setActiveServiceGroup.bind(this);
		this.setServiceCategory = this.setServiceCategory.bind(this);
		this.scrollListener = this.scrollListener.bind(this);
	}
	public componentDidMount() {
		this.props.searchServices();
		this.props.getServiceCategories();
		const url = new URL(window.location.href);
		const existingParams = url.searchParams.get(filterName);
		if (existingParams) {
			genderFilterValue = existingParams as typeof genderFilterValue;
		}
		document.addEventListener('scroll', this.scrollListener);
		this.scrollListener();
	}
	public componentWillUnmount(): void {
		document.removeEventListener('scroll', this.scrollListener);
	}
	public componentDidUpdate(prevProps: IProps) {
		if (prevProps.officeId !== this.props.officeId) {
			this.props.searchServices();
		}
		if (
			servicesIdent(prevProps.publishedServices) !== servicesIdent(this.props.publishedServices) &&
			this.state.chosenVariantParents.length
		) {
			this.setState({ chosenVariantParents: [] });
			this.scrollListener();
		}
	}

	private scrollListener() {
		const { bookableServiceGroups } = this.props;

		if (!bookableServiceGroups) {
			return;
		}

		const tuples = bookableServiceGroups
			.map((serviceGroup) => ({
				element: document.getElementById(generateId(serviceGroup)),
				serviceGroup,
			}))
			.filter((tuple) => tuple.element);

		let bestMatch: { top: number; bottom: number; visibleHeight: number; serviceGroup: IServiceGroup } = null;
		const innerHeight = window.innerHeight;

		for (const tuple of tuples) {
			const rect = tuple.element.getBoundingClientRect();

			if (!rect.height) {
				continue;
			}

			const height = rect.height;
			const top = rect.top - 60; // 60 = header height
			const bottom = rect.bottom - 60;
			let visibleHeight = Math.min(bottom, innerHeight) - Math.max(0, top);
			if (top < 0) visibleHeight = Math.min(bottom, innerHeight);
			if (top > innerHeight) visibleHeight = 0;

			if (!bestMatch || bestMatch.visibleHeight < visibleHeight) {
				bestMatch = {
					top,
					bottom,
					visibleHeight,
					serviceGroup: tuple.serviceGroup,
				};
			}

			if (height === Math.round(visibleHeight)) {
				break;
			}
		}

		if (bestMatch && !this.state.isScrolling) {
			this.setActiveServiceGroup(bestMatch.serviceGroup);
		}
	}

	private get orderLink(): string {
		const serviceTable = this.state.chosenVariantParents.join(',');
		if (!serviceTable) {
			return null;
		}
		return `/bookappointment/${this.props.shopId}/${this.props.officeId}/?services=${encodeURIComponent(serviceTable)}${
			this.props.restrictedMode ? `&${c.RESTRICTED_MODE}=1` : ''
		}`;
	}

	private handleGenderFilter(selectedTab: typeof genderFilterValue) {
		const url = new URL(window.location.href);
		const existingParams = url.searchParams.get(filterName);
		if (existingParams) {
			url.searchParams.set(filterName, selectedTab);
		} else {
			url.searchParams.append(filterName, selectedTab);
		}
		genderFilterValue = selectedTab;
		browserHistory.replace(url.pathname + url.search);
	}

	private toggleService(vpid: string) {
		// Add or remove selected service from array
		const allChosenVariantParents =
			(this.state?.chosenVariantParents?.length && [...this.state.chosenVariantParents]) || [];
		if (!allChosenVariantParents.includes(vpid)) {
			allChosenVariantParents.push(vpid);
		} else {
			const index = allChosenVariantParents.indexOf(vpid);
			// safety if
			if (index > -1) {
				allChosenVariantParents.splice(index, 1);
			}
		}
		this.setState({ chosenVariantParents: [...allChosenVariantParents] });
	}

	private setActiveServiceGroup(activeService: IServiceGroup) {
		this.setState({ activeServiceGroup: activeService.id });
	}

	private scrollToActiveSection(service: IServiceGroup) {
		const id = generateId(service);
		this.setState({ isScrolling: true });

		// MutationObserver is a bit of overkill to use in this case,
		// but after testing with setTimeout and requestAnimationFrame
		// seems to be the most reliable option.
		// Also, the use of setTimeout here is not optimal
		const observer = new MutationObserver(() => {
			if (document.getElementById(id)) {
				const element = document.getElementById(id);

				element.scrollIntoView({
					behavior: 'smooth',
				});

				observer.disconnect();

				setTimeout(() => {
					this.setState({ isScrolling: false });
				}, 300);
			}
		});

		observer.observe(document.getElementById('tabpanel-services'), {
			childList: true,
			subtree: true,
		});
	}

	private setServiceCategory(serviceCategory: IServiceCategoryDomainValue | IServiceCategoryDomainValue['key']) {
		const { bookableServiceGroups } = this.props;
		if (!bookableServiceGroups?.length && !this.availableCategories) {
			return;
		}

		let selectedServiceCategory = typeof serviceCategory === 'object' ? serviceCategory.key : serviceCategory;

		// First time set service category by the correct order
		if (!this.state.selectedServiceCategory) {
			selectedServiceCategory = this.availableCategories[0]?.key;
		}

		this.setState({ selectedServiceCategory });
		const first = bookableServiceGroups.find((sg) => !sg.parentId && sg.serviceCategoryId === selectedServiceCategory);
		if (first) {
			this.setActiveServiceGroup(first);
		}
	}

	private get availableCategoriesIds(): Array<IServiceCategoryDomainValue['key']> {
		const { isFetching, serviceCategories, bookableServiceGroups } = this.props;
		if (isFetching || !serviceCategories?.length || !bookableServiceGroups?.length) {
			return [];
		}
		return bookableServiceGroups.map((sg) => sg.serviceCategoryId).filter((v, i, all) => all.indexOf(v) === i);
	}

	// Optimization: save this in state?
	private get availableCategories(): IServiceCategoryDomainValue[] {
		const { serviceCategories, bookableServiceGroups } = this.props;
		const ids = this.availableCategoriesIds;

		if (!ids.length) {
			return [];
		}

		// Filter service groups that have no parent id and get order of service categories by the order of the service groups
		const sortedBSG = bookableServiceGroups.filter((bsg) => !bsg.parentId).sort((a, b) => a.order - b.order);
		const sortedServiceCategories: number[] = [];
		const orderedServiceCategories: IServiceCategoryDomainValue[] = [];

		sortedBSG.forEach((bsg) => {
			if (!sortedServiceCategories.includes(bsg.serviceCategoryId)) {
				sortedServiceCategories.push(bsg.serviceCategoryId);
			}
		});

		sortedServiceCategories.forEach((ssc) => {
			orderedServiceCategories.push(serviceCategories.find((sc) => ssc === sc.key));
		});

		return orderedServiceCategories.filter((sc) => ids.includes(sc.key));
	}

	private get selectedCategoryKey(): IServiceCategoryDomainValue['key'] | undefined {
		const available = this.availableCategoriesIds;
		if (!available.length) {
			return undefined;
		}
		const ss = this.state.selectedServiceCategory;
		return ss && available.includes(ss) ? ss : available[0];
	}

	private renderMenu(): JSX.Element {
		const { bookableServiceGroups, officeId, shopId, t, isShopOwner } = this.props;
		const renderGroup = (sg: IServiceGroup): JSX.Element => {
			let num = 0; // number of services in parent group
			bookableServiceGroups.map((s) => {
				if (s.parentId && s.parentId === sg.id) {
					num = num + 1;
				}
			});

			return (
				<li
					key={sg.id}
					onClick={() => {
						this.setActiveServiceGroup(sg);
						this.scrollToActiveSection(sg);
					}}
					className={'pointer ' + (this.state.activeServiceGroup === sg.id ? 'active' : '')}
				>
					{sg.value}
					<span className="service-group-number">{num}</span>
				</li>
			);
		};

		const renderCategory = (
			sc: IServiceCategoryDomainValue,
			_i: number,
			all: IServiceCategoryDomainValue[],
		): JSX.Element => {
			const groups = bookableServiceGroups.filter((sg) => sg.serviceCategoryId === sc.key && !sg.parentId);
			if (!groups.length) {
				return <div className="subsection-menu col-lg-2 col-md-0 editServicesContainer"></div>;
			}

			const title = t('DV_ServiceCategories_' + sc.key);
			const list = (
				<div className="sidemenu">
					<ul>{groups.map(renderGroup)}</ul>
				</div>
			);

			if (all.length === 1) {
				return (
					<div className="details" key={sc.key}>
						<div className="summary">
							<strong>{title}</strong>
						</div>
						{list}
					</div>
				);
			}

			const firstService = bookableServiceGroups.find((sg) => !sg.parentId && sg.serviceCategoryId === sc.key);

			return (
				<Expandable
					title={title}
					key={sc.key}
					closed={sc.key !== this.selectedCategoryKey}
					onToggle={(o: boolean) => (o ? this.setServiceCategory(sc) : null)}
					onClick={() => this.scrollToActiveSection(firstService)}
				>
					{list}
				</Expandable>
			);
		};

		return (
			<>
				<div className="col-12 d-block d-lg-none">
					<PortalSelectMenu
						label={t('User_ShopCard_Services_Category_Label')}
						items={createDomainValueOptions(t, 'DV_ServiceCategories_', this.availableCategories, false)}
						input={{
							value: this.selectedCategoryKey,
							onChange: this.setServiceCategory,
						}}
					/>
					<br />
				</div>
				<div className="subsection-menu col-lg-2 col-md-0 editServicesContainer">
					<div className="servicesMenu">
						{this.availableCategories.map(renderCategory)}
						<br />
						{isShopOwner && this.props.bookableServiceGroups?.length ? (
							<Link to={`/shop/${shopId}/${officeId}/edit/services`}>
								<Button>{t('User_ShopCard_Services_EditServices')}</Button>
							</Link>
						) : null}
					</div>
				</div>
			</>
		);
	}
	private renderServiceList(): JSX.Element {
		const { publishedServices, t, isShopOwner, bookableServiceGroups, filteredGroupedServices, shopCard } = this.props;
		const currencyKey = shopCard?.currencyKey;

		if (!publishedServices) {
			return null;
		}
		if (!bookableServiceGroups.length) {
			return (
				<div className="noServicesMessage">
					<img src={require('containers/Html/images/no-services-logo.svg')} />
					<p className="p18">{t('Shop_Shopcard_Services_NoServices')}</p>
					<br />
					{isShopOwner ? (
						<Link to={`/shop/${this.props.shopId}/${this.props.officeId}/edit/services`} className="p14 pointer">
							<Button>{t('User_ShopCard_Services_EditServices')}</Button>
						</Link>
					) : null}
				</div>
			);
		}
		let minSumPrice = 0;
		for (const vpid of this.state.chosenVariantParents) {
			const variations = publishedServices.filter((s) => s.variantParentId === vpid).map((v) => v.price);
			minSumPrice += Math.min(...variations);
		}
		const selectedCategoryKey = this.selectedCategoryKey;
		const shownServiceGroups = bookableServiceGroups.filter(
			(sg) => sg.parentId || sg.serviceCategoryId === selectedCategoryKey,
		);
		// filteredGroupedServices needs change probably
		return (
			<>
				<SelectableServicesList
					groupedServices={groupServices(publishedServices)} // takes current published services
					filteredGroupedServices={filteredGroupedServices} // filtered grouped services by chosen members
					serviceGroups={shownServiceGroups}
					selected={this.state.chosenVariantParents}
					toggle={this.toggleService}
					currencyKey={currencyKey}
					t={t}
				/>
				{this.orderLink ? (
					<Link to={this.orderLink}>
						<Button type="button" className="btnBottom btnBook">
							<span className="count">{this.state.chosenVariantParents.length}</span>
							{t('BookAppointment_BookAppointment')}
							<span className="price">{formatCurrency(currencyKey, minSumPrice)}</span>
						</Button>
					</Link>
				) : null}
			</>
		);
	}
	public render() {
		const { isFetching } = this.props;
		return (
			<LoadingOverlayWrapper active={isFetching}>
				{this.renderMenu()}
				<div className="subsection-main col-lg-8 col-md-12 customOffset servicesContainer">
					{this.renderServiceList()}
				</div>
			</LoadingOverlayWrapper>
		);
	}
}

const mapStateToProps = (state: IStore, ownProps: IOwnProps) => {
	const bookingTemplates = state.shopBookingTemplates;
	const serviceGroupsState = state.serviceGroups;
	const { activeMembers, officeId } = ownProps;
	let publishedServices: IServicesList[];
	let bookableServiceGroups: IServiceGroup[];
	const selectedServices = [];
	let filteredGroupedServices = [];
	if (!bookingTemplates.isFetching && !serviceGroupsState.isFetching) {
		publishedServices = bookingTemplates.services?.filter((s) => s.officeId === officeId && s.published);
		publishedServices = publishedServices.filter((s) => s.resources.length); // Filters services that have no one assigned to it
		// Go through previously defined services and currently active members
		if (activeMembers.length !== 0) {
			publishedServices.map((service) => {
				activeMembers.map((member) => {
					// Check if the member does services
					if (service.resources.map((resource: any) => resource?.id || resource).indexOf(member?.id) !== -1) {
						selectedServices.push(service);
					}
				});
			});
		}
		const filteredSelectedServices = selectedServices.filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i); // Filter duplicates
		publishedServices.sort((a, b) => a.order - b.order); // Sort by order for subservices
		filteredGroupedServices = groupServices(activeMembers?.length === 0 ? publishedServices : filteredSelectedServices);
		const shopServiceGroups = serviceGroupsState.serviceGroups.filter((sg) => sg.officeId === ownProps.officeId);
		bookableServiceGroups = shopServiceGroups.filter((sg) =>
			isBookable(sg.id, shopServiceGroups, filteredGroupedServices),
		);
	}
	return {
		shopCard: state.shopcard?.shopcard,
		publishedServices,
		bookableServiceGroups,
		filteredGroupedServices: filteredGroupedServices,
		isFetching:
			bookingTemplates.isFetching || serviceGroupsState.isFetching || state.domainValues.isFetchingServiceCategories,
		isShopOwner: state.shopcard.isMyShop,
		serviceCategories: state.domainValues.serviceCategories,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<IStore>, ownProps: IOwnProps) => ({
	searchServices: () => {
		dispatch(shopBookingTemplatesGet(ownProps.officeId));
		dispatch(getOfficeServiceGroups(ownProps.officeId));
	},
	getServiceCategories: () => dispatch(domainValuesMap.serviceCategories()),
});

const ShopCardServices = connect(mapStateToProps, mapDispatchToProps)(ShopCardServicesForm);

export { ShopCardServices };
